"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.previewHost = previewHost;
exports.livePreviewUrl = livePreviewUrl;
exports.staticPreviewUrl = staticPreviewUrl;
exports.shareablePreviewUrl = shareablePreviewUrl;
/**
 * How a chat id becomes the hostname of its live sandbox preview.
 *
 * Two places produce this URL — the /preview-url endpoint the client polls and
 * the Slack card posted when a build finishes — and they must agree character
 * for character, because the ingress only answers on one exact host. One
 * definition so the second caller can't drift from the first.
 *
 * NOTE: sandbox.service's ingress builds its host with _sanitizeLabelValue,
 * which maps a different character class and does NOT lowercase. For the ids in
 * use (lowercase hex) every rule agrees; the two only diverge on an id with an
 * uppercase letter or a dot. Left alone deliberately — reconciling them changes
 * which host the ingress answers on, which is a live-traffic change and not
 * this function's job to make.
 */
function previewHost(chatId, previewDomain) {
    return `${chatId.replace(/[^a-z0-9-]/gi, '-').toLowerCase()}.${previewDomain}`;
}
/** `https://<chatId>.<domain>` — the live pod. Undefined when no preview domain
 *  is configured (local dev), where previews are port-forwards with no stable URL. */
function livePreviewUrl(chatId, previewDomain) {
    return previewDomain ? `https://${previewHost(chatId, previewDomain)}` : undefined;
}
/**
 * The `/p/<chatId>` route on studio: proxies to the pod while it's alive and
 * serves the captured MinIO snapshot once it isn't. The link that still resolves
 * after the sandbox is reaped.
 */
function staticPreviewUrl(chatId, apiUrl) {
    return apiUrl ? `${apiUrl.replace(/\/+$/, '')}/p/${encodeURIComponent(chatId)}` : undefined;
}
/**
 * The one link to hand a human: the live pod while it exists, and the durable
 * `/p/<chatId>` route otherwise.
 *
 * The live host is preferred because it is the interactive thing — the pod is
 * up for ~30 min after a build, which is exactly when someone clicks a link in
 * Slack. `/p/` is the fallback rather than the default because it is a proxy in
 * front of a snapshot: always resolvable, never as good while the pod is warm.
 *
 * Undefined only when neither is configured, and callers must then omit the
 * link rather than render a dead one.
 */
function shareablePreviewUrl(chatId, previewDomain, apiUrl) {
    return livePreviewUrl(chatId, previewDomain) ?? staticPreviewUrl(chatId, apiUrl);
}
